import usePropertyService from 'api/services/propertyService'
import { clearCreateAdDraft } from 'api/utils/createAdDraftStorage'
import {
  buildOwnerRegisterPath,
  buildPublishedPath,
  markListingOwnerIntent,
} from 'api/utils/pendingCreateAdFlow'
import PageLoading from 'components/common/loading/PageLoading'
import {
  CreateAdFlowForm,
  isValidCanadianPostalCode,
  mapListingToCreateAdForm,
} from 'components/createAd/createAdFlowState'
import CreateAdRegistrationFlow, {
  type CreateAdSubmitResult,
} from 'components/createAd/CreateAdRegistrationFlow'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import type { CreateAdOwnerRegisterState } from 'pages/CreateAdOwnerRegister'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useAuth } from './authenticated/AuthContext'

interface PropertyResponse {
  success: boolean
  message?: string
  data?: unknown
  requiresAuth?: boolean
  nextAction?: 'login' | 'register'
  email?: string
}

const EDIT_AD_SESSION_KEY = 'easybail.editAd'

type EditAdSession = {
  editPropertyId: string
  returnTo: string
}

function readEditAdSession(): EditAdSession | null {
  try {
    const raw = sessionStorage.getItem(EDIT_AD_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<EditAdSession>
    if (typeof parsed.editPropertyId !== 'string' || !parsed.editPropertyId.trim()) {
      return null
    }
    return {
      editPropertyId: parsed.editPropertyId.trim(),
      returnTo:
        typeof parsed.returnTo === 'string' && parsed.returnTo.trim()
          ? parsed.returnTo
          : '/',
    }
  } catch {
    return null
  }
}

function writeEditAdSession(session: EditAdSession) {
  try {
    sessionStorage.setItem(EDIT_AD_SESSION_KEY, JSON.stringify(session))
  } catch {
    // Ignore.
  }
}

function clearEditAdSession() {
  try {
    sessionStorage.removeItem(EDIT_AD_SESSION_KEY)
  } catch {
    // Ignore.
  }
}

function parseListingId(data: unknown): string | null {
  if (data === null || data === undefined) return null
  if (typeof data === 'number' && Number.isFinite(data)) return String(data)
  if (typeof data === 'string' && data.trim() !== '') return data.trim()
  return null
}

const CreateAd = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const { t } = useTranslation()
  const { registerProperty, loadMyPropertyById } = usePropertyService()
  const { isAuthenticated, user } = useAuth()
  const { refresh: refreshPermissions } = usePermissions()
  const [publishSuccess, setPublishSuccess] = useState<CreateAdSubmitResult | null>(
    null
  )
  const [freshCreateNonce, setFreshCreateNonce] = useState(0)

  const locationState = location.state as {
    editPropertyId?: string
    returnTo?: string
    resumeCreateAd?: boolean
    newListing?: boolean
  } | null

  const editFromState =
    typeof locationState?.editPropertyId === 'string'
      ? locationState.editPropertyId.trim()
      : ''
  const returnToFromState =
    typeof locationState?.returnTo === 'string' && locationState.returnTo.trim()
      ? locationState.returnTo
      : ''
  const wantsFreshCreate = Boolean(locationState?.newListing)

  // Legacy resumeCreateAd without payload used to toast "session expired" and
  // reset the wizard — strip that flag silently.
  useEffect(() => {
    if (!locationState?.resumeCreateAd) return
    navigate('.', {
      replace: true,
      state: {
        editPropertyId: locationState.editPropertyId,
        returnTo: locationState.returnTo,
      },
    })
  }, [locationState, navigate])

  // Explicit "new listing" — never resume a previous edit or draft.
  useEffect(() => {
    if (!wantsFreshCreate) return
    clearEditAdSession()
    clearCreateAdDraft()
    setFreshCreateNonce(n => n + 1)
    navigate('.', { replace: true, state: null })
  }, [wantsFreshCreate, navigate])

  useEffect(() => {
    if (!editFromState) return
    writeEditAdSession({
      editPropertyId: editFromState,
      returnTo: returnToFromState || '/',
    })
  }, [editFromState, returnToFromState])

  const editSession = useMemo(() => {
    // Fresh create must never fall back to a stale edit session.
    if (wantsFreshCreate) return null
    if (editFromState) {
      return {
        editPropertyId: editFromState,
        returnTo: returnToFromState || '/',
      }
    }
    // Survive refresh only while editing (session written when editFromState was present).
    return readEditAdSession()
  }, [editFromState, returnToFromState, wantsFreshCreate])

  const editPropertyId = editSession?.editPropertyId
  const returnTo = editSession?.returnTo || '/'

  const {
    data: editableListing,
    isLoading: isLoadingEditableListing,
    isFetched: isEditableListingFetched,
  } = useQuery(
    ['editableListing', editPropertyId],
    () => loadMyPropertyById(editPropertyId!),
    {
      enabled: Boolean(editPropertyId) && isAuthenticated,
      staleTime: 30_000,
    }
  )

  const editInitialForm: CreateAdFlowForm | null =
    editPropertyId && editableListing
      ? mapListingToCreateAdForm(editableListing, user?.email || '')
      : null

  const { mutateAsync: registerPropertyMutation, isLoading: isRegistering } =
    useMutation<PropertyResponse, Error, Record<string, unknown>>(registerProperty)

  const handleCancel = () => {
    clearEditAdSession()
    navigate(editPropertyId ? returnTo : '/')
  }

  useEffect(() => {
    if (!editPropertyId) return
    if (!isAuthenticated) return
    if (isLoadingEditableListing || !isEditableListingFetched) return
    if (!editableListing) {
      clearEditAdSession()
      toast.error(String(t('createAd.errors.cannotEdit')))
      navigate(returnTo)
    }
  }, [
    editPropertyId,
    editableListing,
    isAuthenticated,
    isEditableListingFetched,
    isLoadingEditableListing,
    navigate,
    returnTo,
    t,
  ])

  const goToOwnerRegister = (
    payload: Record<string, unknown>,
    email: string,
    initialMode: 'register' | 'login' = 'register'
  ) => {
    const state: CreateAdOwnerRegisterState = {
      publishPayload: payload,
      email: email || undefined,
      initialMode,
    }
    navigate(buildOwnerRegisterPath({ email, mode: initialMode }), { state })
  }

  const handleSubmit = async (
    payload: Record<string, unknown>
  ): Promise<CreateAdSubmitResult | void> => {
    try {
      const address = payload.address as { postalCode?: unknown } | undefined
      const postalCode =
        address && typeof address.postalCode === 'string' ? address.postalCode : ''
      if (!isValidCanadianPostalCode(postalCode)) {
        toast.error(t('createAd.flow.validation.invalidPostalCode') as string)
        return
      }

      const email =
        typeof payload.email === 'string' ? payload.email.trim().toLowerCase() : ''

      // Never send a stale edit id on a fresh create.
      const isEdit = Boolean(editPropertyId) && !wantsFreshCreate
      const submitPayload = isEdit ? { ...payload, editPropertyId } : payload

      // Guest → dedicated owner registration page (payload in router state only).
      if (!isAuthenticated) {
        goToOwnerRegister(submitPayload, email, 'register')
        return
      }

      markListingOwnerIntent()
      const response = await registerPropertyMutation(submitPayload)

      if (response?.requiresAuth) {
        goToOwnerRegister(
          submitPayload,
          (typeof response.email === 'string' && response.email.trim()) || email,
          response.nextAction === 'login' ? 'login' : 'register'
        )
        return
      }

      if (!response?.success) {
        throw new Error(response?.message || t('createAd.errors.registerFailed'))
      }

      const listingId =
        parseListingId(response.data) ?? (isEdit ? String(editPropertyId) : null)
      if (!listingId) {
        throw new Error('Listing id missing from server response')
      }

      clearEditAdSession()
      clearCreateAdDraft()
      queryClient.invalidateQueries(['myProperties'])
      // Keep owner intent until email is verified (PermissionsContext clears it).
      await refreshPermissions({ intent: 'list_property' })

      if (isEdit && editPropertyId) {
        queryClient.removeQueries(['editableListing', editPropertyId], {
          exact: true,
        })
        queryClient.invalidateQueries(['loadProperty', editPropertyId])
        queryClient.invalidateQueries(['myListingOwnership', editPropertyId])
      }

      const result: CreateAdSubmitResult = {
        listingId,
        mode: isEdit ? 'updated' : 'created',
      }

      // listingId in the URL so the flow survives refresh (no sessionStorage).
      navigate(buildPublishedPath(listingId, result.mode), {
        replace: true,
        state: result,
      })
      setPublishSuccess(result)
      return result
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('createAd.errors.registerFailed')
      toast.error(message)
      throw error
    }
  }

  useEffect(() => {
    document.title = `${t('createAd.pageTitle')} | Passe Moi Ton Bail`
    return () => {
      document.title = 'Passe Moi Ton Bail'
    }
  }, [t])

  if (editPropertyId) {
    if (!isAuthenticated || isLoadingEditableListing || !editInitialForm) {
      return <PageLoading />
    }
  }

  return (
    <CreateAdRegistrationFlow
      key={
        editPropertyId ? `edit-${editPropertyId}` : `create-${freshCreateNonce}`
      }
      isAuthenticated={isAuthenticated}
      accountEmail={user?.email ?? ''}
      isSubmitting={isRegistering}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      initialForm={editInitialForm}
      initialSuccess={publishSuccess}
      enableDraftPersistence={!editPropertyId}
    />
  )
}

export default CreateAd
